import { END, eventChannel } from '@redux-saga/core';
import {
    all,
    call,
    cancel,
    cancelled,
    put,
    select,
    take,
    takeEvery,
} from '@redux-saga/core/effects';
import { DefaultRootState } from 'react-redux';

import {
    amendLastAsrResult,
    ClientVoiceActionType,
    CommitLastAsrResultAction,
    RollbackLastAsrResultAction,
} from './clientVoice.actions';

function* watchCommitLastAsrResultSaga() {
    yield takeEvery(
        ClientVoiceActionType.CommitLastAsrResult,
        function* (action: CommitLastAsrResultAction) {
            const { payload } = action;
            let last = yield select((state: DefaultRootState) => state.clientVoice.lastAsrResult);

            if (payload.uuid === last?.uuid && payload.data && payload.signature) {
                yield put(amendLastAsrResult({ ...payload }));
                return;
            }

            if (payload.uuid && !last?.uuid) {
                yield put(amendLastAsrResult({ ...payload, loading: true, expired: false }));
                const chan = yield call(countdown, 20_000, 100);

                try {
                    while (true) {
                        // take(END) will cause the saga to terminate by jumping to the finally block
                        yield take(chan);
                        last = yield select(
                            (state: DefaultRootState) => state.clientVoice.lastAsrResult,
                        );

                        if ((payload.uuid === last?.uuid && last.data && last.signature) || !last) {
                            yield cancel();
                        }
                    }
                } finally {
                    if (yield cancelled()) {
                        chan.close();

                        if (last) {
                            yield put(
                                amendLastAsrResult({ ...payload, loading: false, expired: false }),
                            );
                        }
                        return;
                    }

                    if (last) {
                        yield put(
                            amendLastAsrResult({ ...payload, loading: false, expired: true }),
                        );
                    }
                }
            }
        },
    );
}

function* watchRollbackLastAsrResultSaga() {
    yield takeEvery(
        ClientVoiceActionType.RollbackLastAsrResult,
        function* (action: RollbackLastAsrResultAction) {
            const { payload = null } = action;
            yield put(amendLastAsrResult(payload));
        },
    );
}

function countdown(msecs, step = 1000) {
    return eventChannel((emitter) => {
        const timer = setInterval(() => {
            msecs -= step;
            if (msecs > 0) {
                emitter(msecs);
            } else {
                // this causes the channel to close
                emitter(END);
            }
        }, step);
        // The subscriber must return an unsubscribe function
        return () => {
            clearInterval(timer);
        };
    });
}

export function* clientVoiceSaga() {
    yield all([watchCommitLastAsrResultSaga(), watchRollbackLastAsrResultSaga()]);
}
